import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Icon from 'Components/Icon';
import { DateUtils } from 'react-day-picker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import cn from 'classnames';

import moment from 'moment';

import getMaxDate from '../../screens/shared/util/getMaxDate';

const CustomNavBar = ({ onPreviousClick, onNextClick }) => {
  return (
    <div className="DayPicker-NavBar">
      <div className="buttons-container">
        <button
          className="buttons-container__button"
          onClick={(e) => {
            e.preventDefault();
            onPreviousClick();
          }}
        >
          <Icon name="icon-left" />
        </button>
        <div className="buttons-container__inline-separator" />
        <button
          className="buttons-container__button"
          onClick={(e) => {
            e.preventDefault();
            onNextClick();
          }}
        >
          <Icon name="icon-right" />
        </button>
      </div>
    </div>
  );
};

const DatePicker = React.forwardRef(
  (
    {
      dayPickerProps,
      previousDayFrom,
      previousDayTo,
      shouldDisplayFullRange = false,
      onDateChangeCallback,
      availableDays = null,
      className,
      openCalendar,
      callbackHandleOpenCalendar,
      currentAllocation,
    },
    ref
  ) => {
    const [dayFrom, setDayFrom] = useState(null);
    const [dayTo, setDayTo] = useState(null);
    const [isOpened, setIsOpened] = useState(false);
    const [isRangeInvalid, setRangeInvalid] = useState(false);
    const [isOutAllocation, setOutAllocation] = useState(false);

    const selectedRange = useMemo(() => ({ from: dayFrom, to: dayTo }), [dayFrom, dayTo]);

    useEffect(() => {
      setIsOpened(openCalendar);
    }, [openCalendar]);

    const maxDate = useMemo(() => getMaxDate(dayFrom, dayTo, availableDays), [availableDays, dayFrom, dayTo]);

    useEffect(() => {
      if (!isOpened) ref.current.hideDayPicker();
      else ref.current.showDayPicker();
    }, [isOpened, ref]);

    const useOutsideAlerter = useCallback(
      (datePickerRef) => {
        const handleClickOutside = (event) => {
          if (event.target.className === 'DayPickerInput-Fader') {
            datePickerRef.current.hideDayPicker();
            setIsOpened(false);
            callbackHandleOpenCalendar(false);
          }
        };

        const handleInputClick = useCallback(
          (event) => {
            if (event.target === datePickerRef.current.input) {
              setIsOpened(!isOpened);
              if (window.outerWidth < 1024) {
                datePickerRef.current.input.scrollIntoView({ block: 'start', behavior: 'smooth' });
              }
            }
          },
          [datePickerRef]
        );

        useEffect(() => {
          const datePickerRefInput = datePickerRef.current.input;

          document.addEventListener('mousedown', handleClickOutside);
          datePickerRefInput.addEventListener('mousedown', handleInputClick);

          return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            datePickerRefInput.removeEventListener('mousedown', handleInputClick);
          };
        });
      },
      [callbackHandleOpenCalendar, isOpened]
    );

    const applySelectedDate = useCallback(() => {
      setIsOpened(false);

      if (+availableDays === 0) {
        onDateChangeCallback('', '');

        return;
      }
      if (
        typeof dayTo == Date &&
        !moment(dayTo)
          .subtract(availableDays - 1, 'day')
          .isSameOrBefore(moment(dayFrom))
      ) {
        setDayTo(
          moment(dayFrom)
            .add(availableDays - 1, 'days')
            .toDate()
        );
        onDateChangeCallback(
          dayFrom,
          moment(dayFrom)
            .add(availableDays - 1, 'days')
            .toDate()
        );
        return;
      }

      onDateChangeCallback(dayFrom, dayTo);
    }, [availableDays, dayFrom, dayTo, onDateChangeCallback]);

    const CustomDatePickerOverlay = ({ children, classNames, selectedDay, ...props }) => {
      let classFader = classNames.container + '-Fader';

      return (
        <div className={classNames.overlayWrapper} {...props}>
          <div className={classNames.overlay}>
            {children}
            <div className={classFader} />
            {isRangeInvalid && <p className="DayPicker__warning-message">Out of range</p>}
            {isOutAllocation && (
              <p className="DayPicker__warning-message" data-testid="out-of-allocation">
                Out of allocation
              </p>
            )}
            <div className="overlay__buttons-container">
              <button
                className="buttons-container__button _cancel"
                onClick={(e) => {
                  e.preventDefault();
                  ref.current.hideDayPicker();

                  setDayFrom(previousDayFrom);
                  setDayTo(previousDayTo);

                  onDateChangeCallback(previousDayFrom, previousDayTo);
                }}
              >
                Cancel
              </button>
              <button
                data-testid="calendar-apply"
                className="buttons-container__button _apply"
                onClick={(e) => {
                  e.preventDefault();
                  ref.current.hideDayPicker();
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      );
    };

    useOutsideAlerter(ref);

    useEffect(() => {
      setDayFrom(previousDayFrom);
      setDayTo(previousDayTo);
    }, [previousDayFrom, previousDayTo]);

    const checkRangeIsInvalid = useCallback(
      (range) => {
        const lastDay = moment(range.to);
        if (+availableDays === 0 || (range.to && lastDay.isAfter(maxDate))) {
          return true;
        }
        return false;
      },
      [availableDays, maxDate]
    );

    const handleDayRangeClick = useCallback(
      (day) => {
        if (moment(day).isoWeekday() !== 6 && moment(day).isoWeekday() !== 7) {
          const range = DateUtils.addDayToRange(day, { from: dayFrom, to: dayTo });
          setDayFrom(range.from);
          setDayTo(range.to);
          setRangeInvalid(checkRangeIsInvalid(range));

          // set error if date go out allocation range
          const dayTimeReset = moment(day).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          if (range.from === null && range.to === null) {
            setOutAllocation(false);
          } else if (currentAllocation?.validUntil && dayTimeReset.isAfter(moment(currentAllocation?.validUntil))) {
            setOutAllocation(true);
          } else {
            setOutAllocation(false);
          }
        }
      },
      [dayFrom, dayTo, checkRangeIsInvalid, currentAllocation]
    );

    const computeDateInputValue = useCallback(() => {
      if (isOutAllocation || +availableDays === 0) {
        return '';
      } else if (dayFrom && dayTo) {
        const dayToIsAlloved = moment(dayTo).isBefore(maxDate);
        if (dayToIsAlloved) {
          return `${moment(dayFrom).format('MMMM D \u00B7 YYYY')} - ${moment(dayTo).format('MMMM D \u00B7 YYYY')}`;
        } else {
          return `${moment(dayFrom).format('MMMM D \u00B7 YYYY')} - ${moment(maxDate).format('MMMM D \u00B7 YYYY')}`;
        }
      }
    }, [availableDays, dayFrom, dayTo, isOutAllocation, maxDate]);

    const renderDay = useCallback(
      (day, modifiers) => {
        let className = '';

        const isWeekend = moment(day).isoWeekday() === 6 || moment(day).isoWeekday() === 7;

        if (!isWeekend) {
          if (+availableDays === 0) {
            if (DateUtils.isSameDay(day, dayFrom)) {
              className += ' DayPicker-Day--selected--invalid';
            } else if (DateUtils.isSameDay(day, dayTo)) {
              className += ' DayPicker-Day--selected--invalid';
            } else if (dayFrom && dayTo) {
              const isDayInRange = DateUtils.isDayInRange(day, selectedRange);
              if (isDayInRange) {
                className += ' DayPicker-Day--selected--invalid';
              }
            }
          } else {
            if (dayFrom && dayTo) {
              const isDayInRange = DateUtils.isDayInRange(day, selectedRange);

              if (availableDays === null) {
                className += 'DayPicker-Day--selected--valid';
              } else if (isDayInRange) {
                const isValid = isDayInRange && DateUtils.isDayInRange(day, { from: dayFrom, to: maxDate });
                if (isValid) {
                  className += 'DayPicker-Day--selected--valid';
                } else {
                  className += 'DayPicker-Day--selected--invalid';
                }
              }
              const lastValidDate = DateUtils.isDayBefore(maxDate, dayTo) ? maxDate : dayTo;
              if (DateUtils.isSameDay(day, lastValidDate)) {
                className += ' DayPicker-Day--selected--end';
              }
            }

            //  add conditions for available allocation
            const dayInRangeFromTo = DateUtils.isDayInRange(day, { from: dayFrom, to: dayTo });
            if (
              dayInRangeFromTo &&
              currentAllocation?.validUntil &&
              (moment(selectedRange.to).isAfter(currentAllocation?.validUntil) ||
                moment(selectedRange.from).isAfter(currentAllocation?.validUntil))
            ) {
              // default isDayInRange include day in range, we need subtract it
              const isValid = DateUtils.isDayInRange(day, {
                from: moment(dayFrom).subtract(1, 'days').toDate(),
                to: moment(currentAllocation?.validUntil).toDate(),
              });
              if (isValid) {
                className = 'DayPicker-Day--selected--valid';
              } else {
                className = 'DayPicker-Day--selected--invalid';
                !isOutAllocation && setOutAllocation(true);
              }
            }

            if (DateUtils.isSameDay(day, dayFrom)) {
              className += ' DayPicker-Day--selected--start';
            }
          }
        } else {
          className += ' DayPicker-Day--weekend';
        }

        return <div className={className}>{day.getDate()}</div>;
      },
      [availableDays, currentAllocation, dayFrom, dayTo, isOutAllocation, maxDate, selectedRange]
    );

    const handleDayPickerHide = useCallback(() => {
      applySelectedDate();
    }, [applySelectedDate]);

    return (
      <DayPickerInput
        dayPickerProps={{
          className: 'Selectable',
          selectedDays: [dayFrom, selectedRange],
          onDayClick: handleDayRangeClick,
          weekdayElement: ({ className, weekday, localeUtils: { formatWeekdayLong }, locale }) => {
            return <div className={className}>{formatWeekdayLong(weekday, locale).toUpperCase().slice(0, 3)}</div>;
          },
          renderDay: renderDay,
          captionElement: ({ date, locale, localeUtils: { getMonths } }) => {
            const monthName = getMonths(locale)[date.getMonth()];
            const year = date.getFullYear();
            return (
              <div className="DayPicker-Caption">
                <div className="DayPicker-Caption--month"> {monthName} </div>
                <div className="DayPicker-Caption--year"> {year} </div>
              </div>
            );
          },
          navbarElement: CustomNavBar,
          showOutsideDays: true,
          ...dayPickerProps,
        }}
        hideOnDayClick={false}
        overlayComponent={CustomDatePickerOverlay}
        onDayPickerHide={handleDayPickerHide}
        keepFocus={true}
        placeholder={'MMMM D \u00B7 YYYY - MMMM D \u00B7 YYYY'}
        ref={ref}
        component={React.forwardRef((props, ref) => {
          return (
            <input
              {...props}
              autoComplete="none"
              className={cn(className, {
                _focus: isOpened,
              })}
              value={dayFrom || dayTo ? computeDateInputValue() : ''}
              ref={ref}
            />
          );
        })}
      />
    );
  }
);

export default DatePicker;
